
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {LoginInfoRecord} from './LoginInfoRecord';
import {FieldModelLoginInfoRecord} from './LoginInfoRecord';

/**
 * LoginInfoGetResponse struct
 */
class LoginInfoGetResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {number=} lastRecordId
   * @param {!Array=} loginInfoRecords
   * @constructor
   */
  constructor (parent = new Response(), lastRecordId = null, loginInfoRecords = []) {
    super()
    super.copy(parent)
    this.LastRecordId = lastRecordId
    this.LoginInfoRecords = loginInfoRecords
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LoginInfoGetResponse}
   * @param {!LoginInfoGetResponse} other Other struct
   * @returns {!LoginInfoGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.LastRecordId != null) {
      this.LastRecordId = UInt64.fromNumber(other.LastRecordId)
    } else {
      this.LastRecordId = null
    }
    if (other.LoginInfoRecords != null) {
      this.LoginInfoRecords = []
      for (let item of other.LoginInfoRecords) {
        if (item != null) {
          let tempItem
          tempItem = LoginInfoRecord.fromObject(item)
          this.LoginInfoRecords.push(tempItem)
        } else {
          this.LoginInfoRecords.push(null)
        }
      }
    } else {
      this.LoginInfoRecords = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LoginInfoGetResponse}
   * @returns {!LoginInfoGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LoginInfoGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LoginInfoGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LoginInfoGetResponse}
   * @param {!LoginInfoGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LoginInfoGetResponse)) {
      throw new TypeError('Instance of LoginInfoGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LoginInfoGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      LastRecordId: ((this.LastRecordId != null) ? this.LastRecordId.toNumber() : null),
      LoginInfoRecords: ((this.LoginInfoRecords != null) ? Array.from(this.LoginInfoRecords, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LoginInfoGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LoginInfoGetResponse} other Object value
   * @returns {!LoginInfoGetResponse} Created struct
   */
  static fromObject (other) {
    return new LoginInfoGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LoginInfoGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LoginInfoGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LoginInfoGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5174
  }
}

export { LoginInfoGetResponse };

/**
 * Fast Binary Encoding LoginInfoGetResponse field model
 */
class FieldModelLoginInfoGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.LastRecordId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.LoginInfoRecords = new fbe.FieldModelVector(new FieldModelLoginInfoRecord(buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize), buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.LastRecordId.fbeSize + this.LoginInfoRecords.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.LastRecordId.fbeExtra + this.LoginInfoRecords.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLoginInfoGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5174
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LastRecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.LoginInfoRecords.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LoginInfoRecords.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LoginInfoRecords.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!LoginInfoGetResponse} fbeValue Default value, defaults is new LoginInfoGetResponse()
   * @returns {!LoginInfoGetResponse} LoginInfoGetResponse value
   */
  get (fbeValue = new LoginInfoGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!LoginInfoGetResponse} fbeValue LoginInfoGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) <= fbeStructSize) {
      fbeValue.LastRecordId = this.LastRecordId.get()
    } else {
      fbeValue.LastRecordId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.LoginInfoRecords.fbeSize) <= fbeStructSize) {
      this.LoginInfoRecords.get(fbeValue.LoginInfoRecords)
    } else {
      fbeValue.LoginInfoRecords.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LoginInfoRecords.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLoginInfoGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {!LoginInfoGetResponse} fbeValue LoginInfoGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLoginInfoGetResponse}
   * @param {LoginInfoGetResponse} fbeValue LoginInfoGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.LastRecordId.set(fbeValue.LastRecordId)
    this.LoginInfoRecords.set(fbeValue.LoginInfoRecords)
  }
}

export { FieldModelLoginInfoGetResponse };

/**
 * Fast Binary Encoding LoginInfoGetResponse model
 */
class LoginInfoGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLoginInfoGetResponse(this.buffer, 4)
  }

  /**
   * Get the LoginInfoGetResponse model
   * @this {!LoginInfoGetResponseModel}
   * @returns {!FieldModelLoginInfoGetResponse} model LoginInfoGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LoginInfoGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LoginInfoGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LoginInfoGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LoginInfoGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLoginInfoGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LoginInfoGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LoginInfoGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LoginInfoGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LoginInfoGetResponseModel}
   * @param {!LoginInfoGetResponse} value LoginInfoGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LoginInfoGetResponseModel}
   * @param {!LoginInfoGetResponse} value LoginInfoGetResponse value, defaults is new LoginInfoGetResponse()
   * @return {!object} Deserialized LoginInfoGetResponse value and its size
   */
  deserialize (value = new LoginInfoGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LoginInfoGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LoginInfoGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LoginInfoGetResponseModel}
   * @param {!number} prev Previous LoginInfoGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LoginInfoGetResponseModel };
